'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Film,
  Settings,
  ChevronLeft,
  ChevronRight,
  Radio,
  Tv2,
  Clapperboard,
  Webhook,
  Activity,
  ClipboardList,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';


const navItems = [
  {
    key: 'nav-media-library',
    href: '/',
    label: 'Media Library',
    icon: Film,
    badge: null,
  },
  {
    key: 'nav-logs',
    href: '/logs',
    label: 'Webhook Logs',
    icon: ClipboardList,
    badge: null,
  },
  {
    key: 'nav-settings',
    href: '/settings',
    label: 'Settings',
    icon: Settings,
    badge: null,
  },
];

const statusItems = [
  { key: 'status-sonarr', label: 'Sonarr', icon: Tv2, color: 'text-sonarr' },
  { key: 'status-radarr', label: 'Radarr', icon: Clapperboard, color: 'text-radarr' },
  { key: 'status-webhooks', label: 'Webhooks', icon: Webhook, color: 'text-success' },
];

export default function Sidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className="relative flex flex-col h-full border-r border-border transition-all duration-300 ease-in-out flex-shrink-0"
      style={{ width: collapsed ? '64px' : '220px', background: 'var(--card)' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-border min-h-[64px]">
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-sm tracking-tight text-foreground truncate">
            MediaDispatch
          </span>
        )}
      </div>
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="absolute -right-3 top-[72px] z-10 w-6 h-6 rounded-full border border-border flex items-center justify-center btn-ghost"
        style={{ background: 'var(--card)' }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight size={12} />
        ) : (
          <ChevronLeft size={12} />
        )}
      </button>
      {/* Nav */}
      <nav className="flex-1 p-2 pt-3 flex flex-col gap-1">
        {!collapsed && (
          <p className="text-2xs font-semibold tracking-widest text-muted-foreground uppercase px-2 pb-1">
            Navigation
          </p>
        )}
        {navItems?.map((item) => {
          const Icon = item?.icon;
          const isActive = item?.href === '/' ? pathname === '/' : pathname?.startsWith(item?.href);
          return (
            <Link
              key={item?.key}
              href={item?.href}
              title={collapsed ? item?.label : undefined}
              className={`flex items-center gap-3 px-2 py-2 text-sm font-medium rounded-lg transition-all duration-150 ${
                isActive ? 'sidebar-item-active' : 'sidebar-item'
              }`}
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && <span className="truncate">{item?.label}</span>}
              {!collapsed && item?.badge !== null && (
                <span className="ml-auto text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">
                  {item?.badge}
                </span>
              )}
            </Link>
          );
        })}

        {/* Divider */}
        <div className="border-t border-border my-2" />

        {/* Status section */}
        {!collapsed && (
          <p className="text-2xs font-semibold tracking-widest text-muted-foreground uppercase px-2 pb-1">
            Connections
          </p>
        )}
        {statusItems?.map((item) => {
          const Icon = item?.icon;
          return (
            <div
              key={item?.key}
              title={collapsed ? item?.label : undefined}
              className="flex items-center gap-3 px-2 py-1.5 rounded-lg"
            >
              <Icon size={16} className={`flex-shrink-0 ${item?.color}`} />
              {!collapsed && (
                <span className="text-xs text-muted-foreground truncate">{item?.label}</span>
              )}
              {!collapsed && (
                <span className="ml-auto">
                  <Activity size={10} className="text-success" />
                </span>
              )}
            </div>
          );
        })}
      </nav>
      {/* Bottom */}
      <div className="p-3 border-t border-border">
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              <Radio size={14} className="text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-foreground truncate">Personal</p>
              <p className="text-2xs text-muted-foreground truncate">Local instance</p>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
              <Radio size={14} className="text-primary" />
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}